import React from "react";
import {MastheadMenuButton} from "@amzn/meridian/masthead";
import * as AFTXFilterUtils from "@amzn/aftx-common/dist/utils/filter_utils";
import { getAftxCookieFromTag } from "src/auth/aftx-auth";
import { AlertType } from "@amzn/meridian/alert/alert";
import { IS_FC_MENU_REQUEST } from "./fc-utils";

const AFTX_DOMAIN_SUFFIX = 'aftx.amazonoperations.app'

export const isAftxDomain = () => {
    const hostname = window.location.hostname;
    const cleanSuffix = AFTX_DOMAIN_SUFFIX.replace(/\/$/, '');  
    return hostname.endsWith(cleanSuffix);
}

export const IS_AFTX_REQUEST: boolean = isAftxDomain()

export function HideForAftxUsers({children}: { children: React.ReactNode }) {
    if (IS_AFTX_REQUEST) {
        return <></>; // Return empty fragment to not render anything in the DOM
    }
    return <>{children}</>;
}

export const hideEventForAftxUsers = (eventName: string, eventHandler: () => void) => {
    if (IS_AFTX_REQUEST) {
        return {
            [eventName]: (e: Event) => {
                // Prevent default behavior since we want to hide this event for AFT-X users
                e.preventDefault();
            }
        };
    }
    return {
        [eventName]: eventHandler
    }
}
export function hideAttributesForAFTX(value: string) {
    if (IS_AFTX_REQUEST) {
        return getEmptyString();
    }
    return value;

    // Return an empty string for any UI elements(links, labels or any other attributes)
    // that we want to hide for AFT-X users
    function getEmptyString() {
        return "";
    }
}
/**
 * Interface defining the expected props for the `CustomMastheadMenuButton` component.
 */
interface CustomMastheadMenuButtonProps {
    // Define the properties
    onClick: () => void;
    open: boolean;
}
/**
 * Custom Masthead Menu Button
 *
 * This component is a wrapper around the `MastheadMenuButton` component from "@amzn/meridian/masthead", providing additional
 * functionality and type safety for the component's props.
 *
 * @component
 * @example
 * <CustomMastheadMenuButton
 *   onClick={() => handleMenuButton(!isMenuOpen)}
 *   open={true}
 * />
 */
export const CustomMastheadMenuButton = (props: CustomMastheadMenuButtonProps) => {
    /**
     * Determines if the current request is an AFTX request.
     * If true, the component will not render anything.
     */
    if (IS_AFTX_REQUEST || IS_FC_MENU_REQUEST) {
        return <></>
    }
    /**
     * Renders the `MastheadMenuButton` component, passing along the received props.
     */
    return <MastheadMenuButton {...props} />;
};
/**
 * Exposes the `MastheadMenuButton` component as the `WrappedComponent` property of the
 * `CustomMastheadMenuButton` component.
 */
CustomMastheadMenuButton.WrappedComponent = MastheadMenuButton;

const getAftxWarehouse = () => {
    return getAftxCookieFromTag("fcmenu-warehouseId");
};
export const AFTX_WAREHOUSE_ID: string | null = getAftxWarehouse();

// Toast notification configuration for AFTX 4XX errors
export interface AftxToastConfig {
    message: string;
    type: AlertType;
}

// Event name for AFTX 4XX error toast notifications
export const AFTX_ERROR_TOAST_EVENT = 'aftx-error-toast';

// Custom event to show toast notifications for AFTX 4XX errors
export const showAftxErrorToast = (message: string, type: AlertType = 'error') => {
    if (IS_AFTX_REQUEST) {
        const toastEvent = new CustomEvent(AFTX_ERROR_TOAST_EVENT, {
            detail: {
                message,
                type
            } as AftxToastConfig
        });
        window.dispatchEvent(toastEvent);
    }
};
